import { DateTime } from "luxon";
import { CalendarViewType } from "app/models/CalendarPreferences";

interface UseCalendarNavigationParams {
  calendarRef: React.RefObject<any>;
  calendarView: string;
  timeZone: string;
  calendarInterval: { start: DateTime; end: DateTime };
  setCalendarInterval: (interval: { start: DateTime; end: DateTime }) => void;
  currentDay: DateTime;
  setCurrentDay: (day: DateTime) => void;
  updatePreferences: (
    partial: Partial<{ calendarView: CalendarViewType }>,
  ) => void;
}

const useCalendarNavigation = ({
  calendarRef,
  calendarView,
  timeZone,
  calendarInterval,
  setCalendarInterval,
  currentDay,
  setCurrentDay,
  updatePreferences,
}: UseCalendarNavigationParams) => {
  const onDateChangeCalendar = (date: DateTime) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(date.toJSDate());

    if (calendarView === "timeGridNarrow") {
      const startOfWeek = date.startOf("week").setZone(timeZone);
      const endOfWeek = date.endOf("week").setZone(timeZone);
      setCurrentDay(date);
      setCalendarInterval({
        start: startOfWeek,
        end: endOfWeek,
      });
    } else if (calendarView === "timeGridDay" || calendarView === "listWeek") {
      setCurrentDay(date);
    } else {
      setCurrentDay(date);
      setCalendarInterval({
        start: DateTime.fromJSDate(calendarApi.view.currentStart).setZone(
          timeZone,
        ),
        end: DateTime.fromJSDate(calendarApi.view.activeEnd).setZone(timeZone),
      });
    }
  };

  const onNextCalendar = () => {
    const calendarApi = calendarRef.current.getApi();

    if (calendarView === "listWeek") {
      const nextDay = (currentDay || DateTime.now()).plus({ days: 1 });
      setCurrentDay(nextDay);
      return;
    }

    if (calendarView === "timeGridNarrow") {
      const nextWeekStart = calendarInterval.start.plus({ days: 7 });
      const nextWeekDay = currentDay.plus({ days: 7 });
      setCurrentDay(nextWeekDay);
      setCalendarInterval({
        start: nextWeekStart,
        end: nextWeekStart.plus({ days: 6 }),
      });
      calendarApi.gotoDate(nextWeekDay.toJSDate());
    } else {
      calendarApi.next();
      const newDay = DateTime.fromJSDate(calendarApi.view.currentStart).setZone(
        timeZone,
      );
      setCurrentDay(newDay);
    }
  };

  const onPrevCalendar = () => {
    const calendarApi = calendarRef.current.getApi();

    if (calendarView === "listWeek") {
      // Schedule view: jump back by 1 day, but never before today
      const today = DateTime.now().startOf("day");
      const prevDay = (currentDay || DateTime.now()).minus({ days: 1 });
      const clampedDay = prevDay < today ? today : prevDay;
      setCurrentDay(clampedDay);
      return;
    }

    if (calendarView === "timeGridNarrow") {
      const prevWeekStart = calendarInterval.start.minus({ days: 7 });
      const prevWeekDay = currentDay.minus({ days: 7 });
      setCurrentDay(prevWeekDay);
      setCalendarInterval({
        start: prevWeekStart,
        end: prevWeekStart.plus({ days: 6 }),
      });
      calendarApi.gotoDate(prevWeekDay.toJSDate());
    } else {
      calendarApi.prev();
      const newDay = DateTime.fromJSDate(calendarApi.view.currentStart).setZone(
        timeZone,
      );
      setCurrentDay(newDay);
    }
  };

  const onDateChangeCalendarMobile = (date: DateTime) => {
    const calendarApi = calendarRef.current.getApi();

    if (calendarView === "timeGridNarrow") {
      setCurrentDay(date);
    } else {
      setCalendarInterval({
        start: DateTime.fromJSDate(calendarApi.view.currentStart).setZone(
          timeZone,
        ),
        end: DateTime.fromJSDate(calendarApi.view.activeEnd).setZone(timeZone),
      });
    }

    calendarApi.gotoDate(date.toJSDate());
  };

  const onDateChangeDayView = (date: DateTime) => {
    const calendarApi = calendarRef.current.getApi();
    calendarApi.gotoDate(date.toJSDate());
    setCurrentDay(date);
  };

  const onViewChange = (newView: CalendarViewType) => {
    updatePreferences({ calendarView: newView });
    if (newView === "listWeek") {
      setCalendarInterval({ start: null, end: null });
    }
    const url = new URL(window.location.href);
    url.searchParams.set("view", newView);
    window.history.replaceState(null, "", url.pathname + url.search);
  };

  return {
    onNextCalendar,
    onPrevCalendar,
    onDateChangeCalendar,
    onDateChangeCalendarMobile,
    onDateChangeDayView,
    onViewChange,
  };
};

export default useCalendarNavigation;
